import { TextView } from '@atoms';
import { Box, IconButton, Modal, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import {
  callDeleteCoreKnowledgeBase,
  callCreateCoreKnowledgeBase,
  callUpdateCoreKnowledgeBase
} from '@api';
import { toast } from 'react-toastify';
interface FAQModalProps {
  locationId: string;

  open: boolean;
  onClose: () => void;
  operation?: 'add' | 'edit' | 'delete';
  faqId?: string | undefined;
  question?: string | undefined;
  answer?: string | undefined;
  onSuccess?: (message?: string) => void;
}
const FaqModal: React.FC<FAQModalProps> = ({
  locationId,
  open,
  onClose,
  operation,
  faqId,
  question,
  answer,
  onSuccess
}: FAQModalProps) => {
  // State variables
  const [faqQuestion, setFaqQuestion] = React.useState<string>(question || '');
  const [faqAnswer, setFaqAnswer] = React.useState<string>(answer || '');
  // Effect to reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setFaqQuestion(question || '');
      setFaqAnswer(answer || '');
    }
  }, [open, question, answer]);

  // Funtion to handle editing a single FAQ
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'question') {
      setFaqQuestion(e.target.value);
    } else if (e.target.name === 'answer') {
      setFaqAnswer(e.target.value);
    }
  };

  // Function to handle editing a single FAQ
  const editSingleFAQ = () => {
    if (!faqId) {
      toast.error('FAQ ID is required');
      return;
    }

    callUpdateCoreKnowledgeBase({ id: faqId, locationId, question: faqQuestion, answer: faqAnswer })
      .then((response: any) => {
        if (response.code === 200) {
          toast.success('FAQ updated successfully');
          onSuccess?.('FAQ updated successfully');
        } else {
          toast.error('Something went wrong');
        }
      })
      .catch((error: any) => {
        toast.error(`Error updating FAQ: ${error.message}`);
      });
  };

  const addSingleFAQ = () => {
    callCreateCoreKnowledgeBase({ locationId, question: faqQuestion, answer: faqAnswer })
      .then((response: any) => {
        if (response.code === 200 || response.code === 201) {
          toast.success('FAQ added successfully');
          onSuccess?.('FAQ added successfully');
        } else {
          toast.error('Something went wrong');
        }
      })
      .catch((error: any) => {
        toast.error(`Error adding FAQ: ${error.message}`);
      });
  };

  // Function to handle deleting a single FAQ or whole knowledge base
  const deleteSingleFAQ = () => {
    callDeleteCoreKnowledgeBase({ locationId, knowledgeBaseId: faqId })
      .then((response: any) => {
        if (response.code === 200) {
          toast.success('FAQ deleted successfully');
          onSuccess?.('FAQ deleted successfully');
        } else {
          toast.error('Something went wrong');
        }
      })
      .catch((error: any) => {
        toast.error(`Error deleting FAQ: ${error.message}`);
      });
  };

  return (
    <Modal open={open} onClose={onClose}>
      {/* Header */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: 800,
          bgcolor: '#ffffff',
          padding: '1rem 0',
          borderRadius: 2
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: '0 2rem',
            mb: 2,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
          <Typography variant="h6">
            {operation === 'add'
              ? 'Add FAQ'
              : operation === 'edit'
                ? 'Edit FAQ'
                : 'Are you sure you want to delete this FAQ?'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2, bgcolor: 'white', borderRadius: 1, padding: '1rem 2rem' }}>
          {operation === 'add' || operation === 'edit' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                id="outlined-basic"
                label="Question"
                variant="outlined"
                fullWidth
                multiline
                sx={{ mb: 2 }}
                value={faqQuestion || ''}
                name="question"
                onChange={onChange}
              />

              <TextField
                id="outlined-basic"
                label="Answer"
                variant="outlined"
                fullWidth
                multiline
                value={faqAnswer || ''}
                name="answer"
                onChange={onChange}
              />
            </Box>
          ) : (
            <TextView $fontSize={16}>
              This action cannot be undone. Are you sure you want to delete this FAQ?
            </TextView>
          )}
          <Button
            variant="contained"
            color="primary"
            sx={{
              mt: 2,
              mr: 1,
              bgcolor: `${operation === 'delete' ? 'red' : 'primary.main'}`,
              color: `${operation === 'delete' ? '#ffffff' : '#202120'}`,
              '&:hover': {
                backgroundColor: `${operation === 'delete' ? '#d32f2f' : 'primary.main'}`
              }
            }}
            onClick={() => {
              if (operation === 'delete') {
                deleteSingleFAQ();
              } else if (operation === 'edit') {
                editSingleFAQ();
              } else if (operation === 'add') {
                addSingleFAQ();
              }
              onClose();
            }}>
            {operation === 'add' ? 'Add' : operation === 'edit' ? 'Update' : 'Delete'}
          </Button>

          <Button
            variant="contained"
            sx={{ mt: 2, mr: 1 }}
            color="primary"
            onClick={() => onClose()}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default FaqModal;
